import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, MessageSquare } from "lucide-react"
import { Link, useParams, useSearchParams } from "react-router"

import { EmptyState, SkeletonText } from "@/components/ui/alert"
import { Pagination } from "@/components/ui/pagination"
import { formatDateShort } from "@/lib/format-date"

import { evalLabelsQuery, evalRunQuery } from "./api"
import { EvalRunFigures } from "./eval-run-figures"
import { DeleteRunDialog, RenameRunDialog, RunNotes, RunStatusControl } from "./eval-run-manage"
import { EvalRunProfile } from "./eval-run-profile"
import { OUTCOME_TONE } from "./eval-runs-data"

const PAGE_SIZES = [10, 25, 50, 100]

// One run: its figures and its labels. The route is admin-only (canUseDevelopment), so the
// controls are unconditional here.

export function EvalRunPage() {
  const runId = Number(useParams()["runId"])
  const run = useQuery(evalRunQuery(runId))
  const labels = useQuery(evalLabelsQuery(runId))
  // The page and its size live in the URL, so coming back from a label lands on the same page.
  const [params, setParams] = useSearchParams()
  const pageSize = PAGE_SIZES.includes(Number(params.get("size"))) ? Number(params.get("size")) : 25
  const total = labels.data?.length ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const page = Math.min(Math.max(1, Number(params.get("page")) || 1), pageCount)
  const pageRows = labels.data?.slice((page - 1) * pageSize, page * pageSize) ?? []
  const setPaging = (next: { page?: number; size?: number }) => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        if (next.page !== undefined) out.set("page", String(next.page))
        if (next.size !== undefined) out.set("size", String(next.size))
        return out
      },
      { replace: true },
    )
  }

  return (
    <div className="flex h-full flex-col gap-5 overflow-auto px-4 py-5 sm:px-6">
      <Link
        to="/evaluations"
        className="inline-flex w-fit items-center gap-1 text-body-sm text-text-2 hover:text-text"
      >
        <ArrowLeft className="size-4" aria-hidden /> All runs
      </Link>
      {run.isError && (
        <EmptyState reason="error" title="This run could not be loaded" body={run.error.message} />
      )}
      {run.isPending && <SkeletonText lines={2} />}
      {run.isSuccess && (
        <>
          <header className="flex flex-wrap items-start gap-x-4 gap-y-3">
            <div className="min-w-0 flex-1 basis-64">
              <h1 className="text-subtitle text-text">{run.data.name}</h1>
              <p className="mt-0.5 text-caption text-text-3">
                {run.data.dataset} · {run.data.labelCount} labels · measured{" "}
                {formatDateShort(run.data.createdAt)}
                {run.data.serviceRevision ? (
                  <>
                    {" "}
                    on revision <span className="font-mono">{run.data.serviceRevision}</span>
                  </>
                ) : null}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <RunStatusControl run={run.data} />
              <RenameRunDialog run={run.data} />
              <DeleteRunDialog run={run.data} />
            </div>
          </header>
          <RunNotes run={run.data} editable />
          <EvalRunFigures run={run.data} />
          <EvalRunProfile run={run.data} />
        </>
      )}

      <section aria-labelledby="eval-labels" className="pb-8">
        <h2 id="eval-labels" className="mb-2 text-body font-semibold text-text">
          Labels
        </h2>
        {labels.isPending && <SkeletonText lines={5} />}
        {labels.isError && (
          <EmptyState
            reason="error"
            title="The labels could not be loaded"
            body={labels.error.message}
          />
        )}
        {labels.isSuccess && labels.data.length === 0 && (
          <EmptyState title="This run has no labels" />
        )}
        {labels.isSuccess && labels.data.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            <div className="max-h-[calc(100dvh-320px)] overflow-auto">
              <table className="w-full min-w-[640px] text-body-sm">
                <thead className="sticky top-0 z-10 bg-surface-sunken text-left text-caption font-medium text-text-2">
                  <tr>
                    <th className="px-3 py-2">Label</th>
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2 text-right">Caught</th>
                    <th className="px-3 py-2 text-right">Escalated</th>
                    <th className="px-3 py-2 text-right">Missed</th>
                    <th className="px-3 py-2 text-right">Hand-offs</th>
                    <th className="px-3 py-2 text-right">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((label) => (
                    <tr
                      key={label.mid}
                      className="border-t border-border-soft hover:bg-surface-sunken"
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/evaluations/${String(runId)}/labels/${encodeURIComponent(label.mid)}`}
                          className="font-mono text-brand hover:underline"
                        >
                          {label.mid}
                        </Link>
                        <span className="ml-2 text-caption text-text-3">{label.kind}</span>
                      </td>
                      <td className="max-w-80 truncate px-3 py-2 text-text" title={label.fileName}>
                        {label.product}
                      </td>
                      <Count value={label.counts.caught} tone={OUTCOME_TONE.caught} />
                      <Count value={label.counts.escalated} tone={OUTCOME_TONE.escalated} />
                      <Count value={label.counts.missed} tone={OUTCOME_TONE.missed} />
                      <td className="px-3 py-2 text-right tabular-nums">{label.counts.handoffs}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {label.remarkCount > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <MessageSquare className="size-3.5" aria-hidden />
                            {label.remarkCount}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              className="border-t border-border"
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={(p) => {
                setPaging({ page: p })
              }}
              onPageSizeChange={(size) => {
                setPaging({ size })
              }}
              pageSizes={PAGE_SIZES}
            />
          </div>
        )}
      </section>
    </div>
  )
}

function Count({ value, tone }: { value: number; tone: string }) {
  return (
    <td className="px-3 py-2 text-right tabular-nums" style={value ? { color: tone } : undefined}>
      {value}
    </td>
  )
}
